import * as moment from "moment";
import queryString from "query-string";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge, Modal, ModalBody, ModalHeader } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  RSelect,
} from "../../components/Component";
import DatePicker from "../../components/range-date-picker/RangeDatePicker";
import Table from "../../components/react-table/Table";
import { GET, POST } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { USER_ROLES } from "../../utils/Utils";
import { useUser } from "../../context/user";
import UpsertIngredientSchedule from "./UpsertIngredientSchedule";
import { WAREHOUSE_OPTIONS } from "./utils";

const STATUS_MAP = {
  PENDING: { label: "Chờ xử lý", color: "warning" },
  APPROVED: { label: "Đã duyệt", color: "success" },
  COMPLETED: { label: "Hoàn thành", color: "primary" },
  PENDING_CANCEL: { label: "Chờ xác nhận huỷ", color: "orange" },
  CANCELLED: { label: "Đã huỷ", color: "secondary" },
  REJECTED: { label: "Bị từ chối", color: "danger" },
};

const SHIFT_LABEL = {
  "CẢ NGÀY": "Cả ngày",
  SÁNG: "Sáng",
  TRƯA: "Trưa",
  CHIỀU: "Chiều",
};

const STATUS_OPTIONS = [
  { label: "Tất cả", value: "" },
  ...Object.entries(STATUS_MAP).map(([k, v]) => ({ label: v.label, value: k })),
];

const routePath = "/ingredient-schedule";

const IngredientScheduleManager = () => {
  const {
    state: { user: currentUser },
  } = useUser();
  const isManager = [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.ENTRY_STAFF].includes(
    currentUser?.role,
  );

  const history = useHistory();
  const location = useLocation();
  const searchParams = queryString.parse(location.search, {
    arrayFormat: "bracket",
  });

  useEffect(() => {
    if (currentUser && !isManager) history.replace("/");
  }, [currentUser]);

  const clearPagi = useRef();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState({ add: false, isCancel: false });
  const [cancelId, setCancelId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [timeNow, setTimeNow] = useState(Date.now());
  const [smOption, setSmOption] = useState(false);
  const [rangeDate, setRangeDate] = useState({
    fromDate: moment(searchParams?.fromDate || undefined)
      .startOf("days")
      .toDate(),
    toDate: moment(searchParams?.toDate || undefined)
      .endOf("days")
      .toDate(),
  });
  const [filter, setFilter] = useState({
    status: searchParams?.status || "",
  });

  const onRangeChange = (dates) => {
    const [fromDate, toDate] = dates;
    setRangeDate({
      fromDate: fromDate
        ? moment(fromDate).startOf("days").toDate()
        : fromDate,
      toDate: toDate ? moment(toDate).endOf("days").toDate() : toDate,
    });
    if (fromDate && toDate) setTimeNow(moment().valueOf());
  };

  const colTable = [
    { Header: "ID", accessor: "id" },
    {
      Header: "Hàng hoá",
      accessor: (item) =>
        item.ingredientPriceHistory?.ingredient?.name ||
        item.billExternalContract?.ingredient?.name ||
        item.ingredientPriceHistoryId,
    },
    {
      Header: "Khách hàng",
      accessor: (item) =>
        item.ingredientPriceHistory?.customer?.name ||
        item.billExternalContract?.customer?.name ||
        "—",
    },
    { Header: "SL (kg)", accessor: "quantity" },
    {
      Header: "Ngày",
      accessor: (item) =>
        item.startDate ? moment(item.startDate).format("DD/MM/YYYY") : "",
    },
    {
      Header: "Buổi",
      accessor: (item) => SHIFT_LABEL[item.startShift] || item.startShift,
    },
    {
      Header: "Người tạo",
      accessor: (item) => item.createdUser?.name || item.createdBy,
    },
    {
      Header: "Người phụ trách",
      accessor: (item) =>
        item.ingredientPriceHistory?.handler?.name ||
        item.billExternalContract?.handler?.name ||
        item.handler ||
        "—",
    },
    {
      Header: "Kho",
      accessor: (item) =>
        item.warehouseId ? WAREHOUSE_OPTIONS.find((w) => w.value === item.warehouseId)?.label : "—",
    },
    {
      Header: "Nhà VC",
      accessor: (item) =>
        item.deliveryProvider?.name || item.deliveryProviderId || "—",
    },
    {
      Header: "Trạng thái",
      accessor: (item) => {
        const s = STATUS_MAP[item.status] || { label: item.status, color: "secondary" };
        const style = item.status === "PENDING_CANCEL" ? { backgroundColor: "#fd7e14", color: "#fff" } : {};
        return <Badge color={s.color} style={style}>{s.label}</Badge>;
      },
    },
    {
      Header: "Lý do",
      accessor: (item) => item.reason || "—",
    },
    {
      Header: "Hành động",
      accessor: (item) => {
        const isOwner = item.createdBy === currentUser?.id;
        const canCancel = (isOwner || isManager) && !["CANCELLED", "PENDING_CANCEL", "COMPLETED", "REJECTED"].includes(item.status);
        if (!canCancel) return null;
        return (
          <Button
            size="sm"
            color="warning"
            outline
            onClick={() => {
              setCancelId(item.id);
              setCancelReason("");
              toggle("isCancel");
            }}
          >
            <Icon name="cross-circle" />
            <span>Huỷ lịch</span>
          </Button>
        );
      },
    },
  ];

  const getData = () => {
    setLoading(true);
    clearTimeout(clearPagi.current);
    clearPagi.current = setTimeout(() => {
      const params = {
        startDate: moment(rangeDate.fromDate).format("YYYY-MM-DD"),
        endDate: moment(rangeDate.toDate).format("YYYY-MM-DD"),
        ...(filter.status ? { status: filter.status } : {}),
        limit: 200,
      };
      GET(routePath, params, true)
        .then(({ data: res }) => setData(res?.rows || []))
        .finally(() => setLoading(false));
    }, 400);
  };

  useEffect(() => {
    const newParams = queryString.stringify({
      fromDate: moment(rangeDate.fromDate).format("YYYY-MM-DD"),
      toDate: moment(rangeDate.toDate).format("YYYY-MM-DD"),
      ...(filter.status ? { status: filter.status } : {}),
    });
    history.replace({ pathname: location.pathname, search: newParams });
    getData();
  }, [timeNow, filter]);

  const toggle = (type) => setView((v) => ({ ...v, [type]: !v[type] }));

  const resetData = () => {
    setCancelId(null);
    setCancelReason("");
    setView({ add: false, isCancel: false });
    setTimeNow(Date.now());
  };

  const handleCancel = () => {
    if (!cancelReason.trim()) return toast.error("Vui lòng nhập lý do huỷ");
    POST(`${routePath}/${cancelId}/cancel`, { reason: cancelReason.trim() }, {}, true)
      .then(() => {
        toast.success("Huỷ lịch thành công");
        resetData();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Có lỗi xảy ra")
      }

      );
  };

  return (
    <React.Fragment>
      <Head title="Lịch hàng" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Quản lý lịch hàng</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v" />
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end align-items-end flex-wrap">
                    <li>
                      <div className="form-group mb-0">
                        <DatePicker
                          selected={rangeDate.fromDate}
                          startDate={rangeDate.fromDate}
                          onChange={onRangeChange}
                          endDate={rangeDate.toDate}
                          selectsRange
                          className="form-control date-picker"
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-group mb-0" style={{ minWidth: 150 }}>
                        <RSelect
                          options={STATUS_OPTIONS}
                          value={
                            STATUS_OPTIONS.find(
                              (o) => o.value === filter.status,
                            ) || STATUS_OPTIONS[0]
                          }
                          onChange={(selected) =>
                            setFilter((f) => ({
                              ...f,
                              status: selected?.value || "",
                            }))
                          }
                          placeholder="Trạng thái"
                        />
                      </div>
                    </li>
                    {isManager && (
                      <li>
                        <Button color="primary" onClick={() => toggle("add")}>
                          <Icon name="plus" />
                          <span>Tạo lịch</span>
                        </Button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div className="my-2">
                <Table
                  loading={loading}
                  columns={colTable}
                  data={data}
                  notShowPagi={true}
                />
              </div>
            </div>
          </div>
        </Block>

        <UpsertIngredientSchedule
          isOpen={view.add}
          onClose={resetData}
          onSuccess={resetData}
        />

        <Modal isOpen={view.isCancel} toggle={() => toggle("isCancel")}>
          <ModalHeader toggle={() => toggle("isCancel")}>Huỷ lịch hàng</ModalHeader>
          <ModalBody>
            <div className="form-group mb-3">
              <label className="form-label">
                Lý do huỷ <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Nhập lý do huỷ..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button size="lg" color="light" onClick={() => toggle("isCancel")}>
                Đóng
              </Button>
              <Button size="lg" color="warning" onClick={handleCancel}>
                Xác nhận huỷ
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default IngredientScheduleManager;
